import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { FloppyDisk, ArrowUp, ArrowDown, Trash, Plus, PlayCircle, PauseCircle, Users, ArrowLeft, EnvelopeSimple, Clock, ChatCircle, CheckSquare, Lightning, Tag, Bell, GitBranch, PlugsConnected } from "@phosphor-icons/react";

const STEP_TYPES = [
  { id: "email", label: "Send Email", icon: EnvelopeSimple, color: "var(--accent)" },
  { id: "wait", label: "Wait", icon: Clock, color: "var(--text-2)" },
  { id: "sms", label: "Send SMS", icon: ChatCircle, color: "#34C759" },
  { id: "task", label: "Create Task", icon: CheckSquare, color: "#5AC8FA" },
  { id: "stage_change", label: "Change Stage", icon: Lightning, color: "#FF9500" },
  { id: "add_tag", label: "Add Tag", icon: Tag, color: "#AF52DE" },
  { id: "remove_tag", label: "Remove Tag", icon: Tag, color: "var(--text-3)" },
  { id: "notification", label: "Notify Me", icon: Bell, color: "#FFD60A" },
  { id: "condition", label: "If / Then", icon: GitBranch, color: "#FF3B30" },
  { id: "webhook", label: "Webhook", icon: PlugsConnected, color: "var(--text-3)" },
];

const TRIGGER_TYPES = [
  { id: "manual", label: "Manually enrolled" },
  { id: "contact_created", label: "When a new contact is added" },
  { id: "stage_changed", label: "When stage changes" },
  { id: "tag_added", label: "When a tag is added" },
  { id: "event", label: "When captured at an event" },
  { id: "import", label: "When imported via CSV" },
  { id: "referral", label: "When added as a referral" },
  { id: "meeting_completed", label: "When a meeting completes" },
];

const CONDITION_TYPES = [
  { id: "contact_replied", label: "Contact replied?", value_type: "boolean" },
  { id: "meeting_booked", label: "Meeting booked?", value_type: "boolean" },
  { id: "stage_equals", label: "Stage equals", value_type: "stage" },
  { id: "tag_exists", label: "Has tag", value_type: "text" },
  { id: "days_since_last_contact", label: "Days since last contact", value_type: "number" },
  { id: "relationship_type", label: "Relationship type", value_type: "relationship" },
  { id: "priority", label: "Priority", value_type: "priority" },
];

export default function CampaignBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/automation/campaigns/${id}`).then((r) => setC(r.data));
  useEffect(() => { load(); }, [id]);

  if (!c) return <div className="p-10 text-sm" style={{ color: "var(--text-2)" }}>Loading…</div>;

  const update = (patch) => setC({ ...c, ...patch });
  const updateStep = (i, patch) => {
    const steps = c.steps.map((s, idx) => idx === i ? { ...s, ...patch } : s);
    setC({ ...c, steps });
  };
  const updateStepConfig = (i, patch) => {
    const steps = c.steps.map((s, idx) => idx === i ? { ...s, configuration: { ...(s.configuration || {}), ...patch } } : s);
    setC({ ...c, steps });
  };
  const addStep = (step_type = "email") => {
    const steps = [...c.steps, {
      step_order: c.steps.length,
      step_type,
      delay_amount: step_type === "wait" ? 1 : 2,
      delay_unit: "days",
      configuration: step_type === "email" ? { subject: "Hi {{first_name}}", body: "" } :
                    step_type === "condition" ? { condition_type: "contact_replied", operator: "eq", value: true, if_true_exit: true } :
                    {},
    }];
    setC({ ...c, steps });
  };
  const moveStep = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= c.steps.length) return;
    const steps = [...c.steps];
    [steps[i], steps[j]] = [steps[j], steps[i]];
    steps.forEach((s, idx) => s.step_order = idx);
    setC({ ...c, steps });
  };
  const removeStep = (i) => {
    const steps = c.steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, step_order: idx }));
    setC({ ...c, steps });
  };

  const save = async (newStatus) => {
    setSaving(true);
    try {
      await api.put(`/automation/campaigns/${id}`, {
        name: c.name,
        description: c.description,
        category: c.category,
        trigger_type: c.trigger_type,
        trigger_config: c.trigger_config,
        stop_on_reply: !!c.stop_on_reply,
        continue_on_reply: !!c.continue_on_reply,
        status: newStatus || c.status,
        steps: c.steps.map((s) => ({
          step_order: s.step_order,
          step_type: s.step_type,
          delay_amount: Number(s.delay_amount || 0),
          delay_unit: s.delay_unit || "days",
          configuration: s.configuration || {},
        })),
      });
      toast.success(newStatus ? `Campaign ${newStatus}` : "Saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="campaign-builder-page">
      <Toaster theme="dark" />
      <button onClick={() => nav("/campaigns")} className="btn-secondary mb-6 flex items-center gap-2" data-testid="back-to-campaigns">
        <ArrowLeft weight="bold" /> All campaigns
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div className="flex-1 min-w-0">
          <input
            data-testid="campaign-name-input"
            className="input-base font-display font-black text-3xl md:text-4xl tracking-tighter"
            style={{ background: "transparent", border: "none", padding: 0 }}
            value={c.name}
            onChange={(e) => update({ name: e.target.value })}
          />
          <input
            data-testid="campaign-desc-input"
            className="input-base mt-2 text-sm"
            style={{ background: "transparent", border: "none", padding: 0, color: "var(--text-2)" }}
            placeholder="Short description…"
            value={c.description || ""}
            onChange={(e) => update({ description: e.target.value })}
          />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="tag" style={{ color: statusColor(c.status), borderColor: statusColor(c.status) }}>{c.status}</span>
          <button onClick={() => save()} disabled={saving} className="btn-secondary flex items-center gap-2" data-testid="save-draft-btn">
            <FloppyDisk weight="bold" /> Save
          </button>
          {c.status !== "active" ? (
            <button onClick={() => save("active")} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="activate-btn">
              <PlayCircle weight="bold" /> Activate
            </button>
          ) : (
            <button onClick={() => save("paused")} disabled={saving} className="btn-secondary flex items-center gap-2" data-testid="pause-btn">
              <PauseCircle weight="bold" /> Pause
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Trigger + settings */}
        <section className="lg:col-span-4">
          <div className="border p-5 mb-6" style={{ borderColor: "var(--border)" }}>
            <div className="overline mb-3">// trigger</div>
            <select
              className="input-base"
              style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }}
              value={c.trigger_type || "manual"}
              onChange={(e) => update({ trigger_type: e.target.value })}
              data-testid="trigger-select"
            >
              {TRIGGER_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <div className="text-xs mt-2" style={{ color: "var(--text-3)" }}>
              How contacts enter this campaign.
            </div>
          </div>

          <div className="border p-5 mb-6" style={{ borderColor: "var(--border)" }}>
            <div className="overline mb-3">// stop conditions</div>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={!!c.stop_on_reply} onChange={(e) => update({ stop_on_reply: e.target.checked })} className="mt-1" data-testid="stop-on-reply" />
              <span><b>Stop when contact replies</b><div className="text-xs mt-1" style={{ color: "var(--text-3)" }}>Recommended — don't keep pestering someone who already answered.</div></span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer text-sm mt-3">
              <input type="checkbox" checked={!!c.continue_on_reply} onChange={(e) => update({ continue_on_reply: e.target.checked, stop_on_reply: !e.target.checked })} className="mt-1" data-testid="continue-on-reply" />
              <span><b>Continue through replies</b><div className="text-xs mt-1" style={{ color: "var(--text-3)" }}>Advanced — only check this if your campaign is non-conversational (e.g. drip education).</div></span>
            </label>
          </div>

          <div className="border p-5" style={{ borderColor: "var(--border)" }}>
            <div className="overline mb-3">// add step</div>
            <div className="grid grid-cols-2 gap-2">
              {STEP_TYPES.map((st) => (
                <button key={st.id} onClick={() => addStep(st.id)} className="btn-secondary text-xs flex items-center gap-2 justify-start" data-testid={`add-${st.id}`}>
                  <st.icon weight="bold" style={{ color: st.color }} /> {st.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="lg:col-span-8">
          <div className="overline mb-4">// {c.steps.length} step{c.steps.length === 1 ? "" : "s"}</div>
          <div className="space-y-3">
            {c.steps.map((s, i) => (
              <StepCard
                key={i}
                index={i}
                step={s}
                onChange={(patch) => updateStep(i, patch)}
                onConfig={(patch) => updateStepConfig(i, patch)}
                onMoveUp={() => moveStep(i, -1)}
                onMoveDown={() => moveStep(i, 1)}
                onRemove={() => removeStep(i)}
              />
            ))}
            {c.steps.length === 0 && (
              <div className="border p-6 text-sm text-center" style={{ borderColor: "var(--border)", color: "var(--text-2)" }}>
                Add your first step on the left.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function statusColor(s) {
  return { draft: "var(--text-3)", active: "var(--accent)", paused: "#FF9500", archived: "var(--text-3)" }[s] || "var(--text-3)";
}

function StepCard({ index, step, onChange, onConfig, onMoveUp, onMoveDown, onRemove }) {
  const meta = STEP_TYPES.find((t) => t.id === step.step_type) || STEP_TYPES[0];
  const Icon = meta.icon;
  return (
    <div className="border p-4" style={{ borderColor: "var(--border)" }} data-testid={`step-${index}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className="font-mono text-xs" style={{ color: "var(--text-3)" }}>#{index + 1}</span>
          <Icon weight="bold" size={20} style={{ color: meta.color }} />
          <div className="font-semibold">{meta.label}</div>
        </div>
        <div className="flex gap-1">
          <button onClick={onMoveUp} className="btn-secondary text-xs px-2" data-testid={`move-up-${index}`}><ArrowUp weight="bold" /></button>
          <button onClick={onMoveDown} className="btn-secondary text-xs px-2" data-testid={`move-down-${index}`}><ArrowDown weight="bold" /></button>
          <button onClick={onRemove} className="btn-secondary text-xs px-2" data-testid={`remove-step-${index}`}><Trash weight="bold" /></button>
        </div>
      </div>

      {/* Delay row */}
      <div className="flex items-center gap-2 mb-3">
        <span className="overline">wait</span>
        <input type="number" min="0" value={step.delay_amount}
          onChange={(e) => onChange({ delay_amount: Number(e.target.value) })}
          className="input-base" style={{ width: 80, padding: "6px 10px" }} data-testid={`delay-amount-${index}`} />
        <select value={step.delay_unit || "days"} onChange={(e) => onChange({ delay_unit: e.target.value })}
          className="input-base" style={{ width: 110, background: "var(--surface-2)", border: "1px solid var(--border)", padding: "6px 10px" }}
          data-testid={`delay-unit-${index}`}>
          {["seconds", "minutes", "hours", "days", "weeks"].map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <span className="overline">after previous step</span>
      </div>

      {/* Type-specific config */}
      {step.step_type === "email" && (
        <div className="space-y-2">
          <input className="input-base" placeholder="Subject (merge tags OK)" value={step.configuration?.subject || ""} onChange={(e) => onConfig({ subject: e.target.value })} data-testid={`email-subject-${index}`} />
          <textarea className="input-base" rows={4} placeholder="Body — use {{first_name}}, {{company}}, {{user_first_name}}, {{meeting_link}}" value={step.configuration?.body || ""} onChange={(e) => onConfig({ body: e.target.value })} data-testid={`email-body-${index}`} />
        </div>
      )}
      {step.step_type === "sms" && (
        <textarea className="input-base" rows={2} placeholder="SMS body (max ~160 chars)" value={step.configuration?.body || ""} onChange={(e) => onConfig({ body: e.target.value })} data-testid={`sms-body-${index}`} />
      )}
      {step.step_type === "task" && (
        <div className="grid grid-cols-2 gap-2">
          <input className="input-base" placeholder="Task title" value={step.configuration?.title || ""} onChange={(e) => onConfig({ title: e.target.value })} data-testid={`task-title-${index}`} />
          <select className="input-base" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} value={step.configuration?.priority || "normal"} onChange={(e) => onConfig({ priority: e.target.value })} data-testid={`task-priority-${index}`}>
            {["low", "normal", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      )}
      {step.step_type === "stage_change" && (
        <select className="input-base" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} value={step.configuration?.stage || "warming"} onChange={(e) => onConfig({ stage: e.target.value })} data-testid={`stage-select-${index}`}>
          {["new", "warming", "engaged", "active", "dormant"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
      {(step.step_type === "add_tag" || step.step_type === "remove_tag") && (
        <input className="input-base" placeholder="Tag name" value={step.configuration?.tag_name || ""} onChange={(e) => onConfig({ tag_name: e.target.value })} data-testid={`tag-name-${index}`} />
      )}
      {step.step_type === "notification" && (
        <input className="input-base" placeholder="What should the notification say?" value={step.configuration?.title || ""} onChange={(e) => onConfig({ title: e.target.value })} data-testid={`notif-title-${index}`} />
      )}
      {step.step_type === "webhook" && (
        <input className="input-base" placeholder="https://your.webhook/url" value={step.configuration?.url || ""} onChange={(e) => onConfig({ url: e.target.value })} data-testid={`webhook-url-${index}`} />
      )}
      {step.step_type === "condition" && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <select className="input-base" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} value={step.configuration?.condition_type || "contact_replied"} onChange={(e) => onConfig({ condition_type: e.target.value })} data-testid={`cond-type-${index}`}>
              {CONDITION_TYPES.map((ct) => <option key={ct.id} value={ct.id}>{ct.label}</option>)}
            </select>
            <select className="input-base" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} value={step.configuration?.operator || "eq"} onChange={(e) => onConfig({ operator: e.target.value })} data-testid={`cond-op-${index}`}>
              {["eq", "ne", "gt", "gte", "lt", "lte", "contains"].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <input className="input-base" placeholder="Value" value={step.configuration?.value ?? ""} onChange={(e) => onConfig({ value: e.target.value })} data-testid={`cond-val-${index}`} />
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={!!step.configuration?.if_true_exit} onChange={(e) => onConfig({ if_true_exit: e.target.checked })} data-testid={`cond-exit-${index}`} />
            <span>If condition matches, <b>exit campaign</b>.</span>
          </label>
        </div>
      )}
    </div>
  );
}
